import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getArenaSocket } from './arenaSocket';
import { buildScout } from './scouting';

const SESSION_SECS = 180; // fallback only — the real clock comes from the server (sessionEndsAt)
const SLEEPERCDN = 'https://sleepercdn.com/content/nfl/players/thumb/';

/* ════════ The Taco Stand — the room ════════
   Layout: YOUR roster on the left (what you give), THEIR roster on the right
   (what you get), the offer ticket in the middle. Functional labels are literal;
   the taco personality lives in the chat, stamps, and flavor moments. */
const styles = `
  .ar-page { display:flex; flex-direction:column; height:calc(100vh - 60px); overflow:hidden; background:#fff;
    font-family:-apple-system,BlinkMacSystemFont,'SF Pro Display','Helvetica Neue',Arial,sans-serif;
    --brand:#FF7A00; --brand-soft:rgba(255,122,0,.13); --brand-ink:#C25E00; --ink:#1D1D1F; --ink2:#6E6E73; --ink3:#86868B;
    --surface:#F5F5F7; --surface2:#EBEBED; --green:#34C759; --green-soft:rgba(52,199,89,.14); --green-ink:#1B873F;
    --hair:#EBEBED; --disabled:#D2D2D7; --cream:#FFF8F1; --paper:#FFFDF8; --paper-edge:#EFE4D2; --mono:'SF Mono',ui-monospace,'Menlo',monospace; --ease:cubic-bezier(0.16,1,0.3,1); }
  .ar-page * { box-sizing:border-box; }
  .ar-page button { font:inherit; color:inherit; background:none; border:none; cursor:pointer; }

  /* game bar */
  .ar-gamebar { height:66px; flex:none; display:flex; align-items:center; padding:0 18px; border-bottom:1px solid var(--hair); background:rgba(255,255,255,.92); }
  .ar-gp { display:flex; align-items:center; gap:10px; width:240px; }
  .ar-gp.right { justify-content:flex-end; }
  .ar-gp-ava { width:34px; height:34px; border-radius:50%; background:var(--surface); display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; color:var(--ink2); overflow:hidden; flex:none; }
  .ar-gp-ava img { width:100%; height:100%; object-fit:cover; }
  .ar-gp-id .nm { font-size:14px; font-weight:600; color:var(--ink); }
  .ar-gp-id .mt { font-size:11px; color:var(--ink3); }
  .ar-gp-id.right { text-align:right; }
  .ar-center-bar { flex:1; display:flex; flex-direction:column; align-items:center; gap:1px; }
  .ar-round { font-size:10px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:var(--brand-ink); }
  .ar-round.served { color:var(--green-ink); }
  .ar-timer { font-size:28px; font-weight:700; letter-spacing:-.03em; font-variant-numeric:tabular-nums; line-height:1; color:var(--ink); }
  .ar-timer.urgent { color:#FF3B30; animation:arpulse 1s ease-in-out infinite; }
  @keyframes arpulse { 0%,100% { transform:scale(1); } 50% { transform:scale(1.07); } }
  .ar-timer small { display:block; font-size:9px; font-weight:600; letter-spacing:.12em; text-transform:uppercase; color:var(--ink3); text-align:center; margin-top:2px; }
  .ar-page .ar-quit { font-size:12px; color:var(--ink3); padding:6px 10px; border-radius:8px; }
  .ar-page .ar-quit:hover { background:var(--surface); color:#FF3B30; }

  /* body */
  .ar-body { flex:1; display:flex; overflow:hidden; min-height:0; }

  /* roster panes */
  .ar-menu { width:280px; flex:none; display:flex; flex-direction:column; overflow:hidden; }
  .ar-menu.left { border-right:1px solid var(--hair); }
  .ar-menu.right { border-left:1px solid var(--hair); }
  .ar-menu-head { padding:11px 14px; flex:none; border-bottom:1px solid var(--hair); display:flex; align-items:center; gap:10px; }
  .ar-menu.left .ar-menu-head { background:var(--brand-soft); border-bottom-color:rgba(255,122,0,.22); box-shadow:inset 3px 0 0 var(--brand); }
  .ar-mh-ava { width:38px; height:38px; border-radius:50%; background:var(--surface2); flex:none; overflow:hidden; display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:700; color:var(--ink2); }
  .ar-mh-ava img { width:100%; height:100%; object-fit:cover; }
  .ar-mh-ava.taco { background:radial-gradient(circle at 50% 38%,#FFD9A8,#FFB55C); font-size:20px; }
  .ar-mh-id { min-width:0; flex:1; }
  .ar-mh-row { display:flex; align-items:center; gap:7px; min-width:0; }
  .ar-mh-tag { font-size:9px; font-weight:800; letter-spacing:.08em; padding:2px 7px; border-radius:980px; flex:none; }
  .ar-mh-tag.you { background:var(--brand); color:#fff; }
  .ar-mh-tag.them { background:var(--surface2); color:var(--ink2); }
  .ar-mh-k { font-size:10px; font-weight:600; letter-spacing:.06em; text-transform:uppercase; color:var(--ink3); margin-top:3px; }
  .ar-mh-name { font-size:15px; font-weight:600; color:var(--ink); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .ar-filters { display:flex; gap:4px; padding:8px 12px 6px; flex-wrap:wrap; flex-none; }
  .ar-page .ar-fpill { font-size:10px; font-weight:600; padding:3px 9px; border-radius:980px; border:1.5px solid var(--disabled); color:var(--ink2); background:#fff; }
  .ar-page .ar-fpill.on { background:var(--ink); border-color:var(--ink); color:#fff; }
  .ar-scroll { flex:1; overflow-y:auto; padding:4px 12px 12px; }
  .ar-posgroup { margin-bottom:12px; }
  .ar-poslabel { font-size:9px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:var(--ink3); margin:0 0 5px 2px; }
  .ar-row { display:flex; align-items:center; gap:9px; background:var(--surface); border-radius:11px; padding:8px 10px; margin-bottom:5px; cursor:pointer; transition:background .15s, transform .15s var(--ease); }
  .ar-row:hover { background:var(--surface2); transform:translateY(-1px); }
  .ar-row:active { transform:translateY(0) scale(.98); }
  .ar-menu.left .ar-row:hover { box-shadow:inset 0 0 0 1.5px var(--brand); }
  .ar-menu.right .ar-row:hover { box-shadow:inset 0 0 0 1.5px var(--green); }
  .ar-row.in { background:var(--ink); animation:arselect .28s var(--ease); }
  @keyframes arselect { 0% { transform:scale(1); } 45% { transform:scale(1.035); } 100% { transform:scale(1); } }
  .ar-row.in .ar-p-name { color:#fff; }
  .ar-row.in .ar-p-meta { color:rgba(255,255,255,.45); }
  .ar-row.in .ar-av { background:rgba(255,255,255,.16); color:rgba(255,255,255,.7); }
  .ar-row.in .ar-add { color:#fff; }
  .ar-av { width:34px; height:34px; border-radius:50%; background:var(--disabled); flex:none; overflow:hidden; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; color:#fff; }
  .ar-av img { width:100%; height:100%; object-fit:cover; }
  .ar-av.pick { background:var(--surface2); color:var(--ink3); font-family:var(--mono); font-size:9px; line-height:1.05; text-align:center; flex-direction:column; }
  .ar-av.sm { width:30px; height:30px; }
  .ar-av.xs { width:22px; height:22px; font-size:8px; }
  .ar-av.lg { width:32px; height:32px; }
  .ar-p-info { flex:1; min-width:0; }
  .ar-p-name { font-size:13px; font-weight:600; color:var(--ink); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .ar-p-meta { font-size:10px; color:var(--ink3); margin-top:1px; }
  .ar-add { margin-left:auto; font-size:14px; color:var(--ink3); flex:none; }
  .ar-row-tag { flex:none; font-size:8.5px; font-weight:800; letter-spacing:.05em; border-radius:980px; padding:2px 6px; }
  .ar-row-tag.target { background:var(--green-soft); color:var(--green-ink); }
  .ar-row-tag.excess { background:var(--brand-soft); color:var(--brand-ink); }
  .ar-row.in .ar-row-tag { background:rgba(255,255,255,.18); color:#fff; }

  /* center */
  .ar-center { flex:1; min-width:0; background:var(--cream); display:flex; flex-direction:column; overflow:hidden; position:relative; }
  .ar-counter-banner { background:var(--brand-soft); color:var(--brand-ink); padding:9px 16px; font-size:12px; font-weight:500; display:flex; align-items:center; gap:8px; flex:none; animation:arbannerin .3s var(--ease) both; }
  @keyframes arbannerin { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:none; } }
  .ar-page .ar-counter-cancel { margin-left:auto; font-size:11px; color:var(--brand-ink); text-decoration:underline; }

  /* incoming */
  .ar-incoming { flex:none; padding:14px 18px 6px; max-height:42%; overflow-y:auto; }
  .ar-inc-head { display:flex; align-items:center; gap:8px; margin-bottom:9px; }
  .ar-inc-k { font-size:10px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:var(--ink3); }
  .ar-inc-count { display:inline-flex; align-items:center; justify-content:center; background:var(--brand); color:#fff; font-size:10px; font-weight:700; min-width:18px; height:18px; border-radius:980px; padding:0 5px; }
  .ar-inc-empty { font-size:13px; color:var(--ink3); padding:4px 0 10px; }
  .ar-inc-card { background:#fff; border:1px solid var(--paper-edge); border-radius:14px; box-shadow:0 8px 24px rgba(0,0,0,.07); overflow:hidden; margin-bottom:10px; animation:arincin .45s var(--ease) both; }
  @keyframes arincin { from { opacity:0; transform:translateY(-10px) scale(.98); } to { opacity:1; transform:none; } }
  .ar-inc-strip { height:6px; background:repeating-linear-gradient(90deg,var(--brand) 0 18px,#FFE7CC 18px 36px); }
  .ar-inc-from { font-family:var(--mono); font-size:11px; color:var(--ink3); padding:9px 14px 0; }
  .ar-inc-grid { display:flex; align-items:stretch; padding:10px 14px 12px; }
  .ar-inc-col { flex:1; min-width:0; }
  .ar-inc-col-k { font-size:9px; font-weight:700; letter-spacing:.09em; text-transform:uppercase; margin-bottom:8px; }
  .ar-inc-col.get .ar-inc-col-k { color:var(--green-ink); }
  .ar-inc-col.give .ar-inc-col-k { color:var(--ink2); }
  .ar-inc-asset { display:flex; align-items:center; gap:8px; margin-bottom:7px; }
  .ar-ia-name { font-size:12.5px; font-weight:600; color:var(--ink); }
  .ar-ia-meta { font-size:10px; color:var(--ink3); }
  .ar-inc-for { flex:none; width:40px; display:flex; align-items:center; justify-content:center; color:var(--disabled); font-size:16px; }
  .ar-inc-actions { display:flex; align-items:center; gap:8px; padding:12px 14px; border-top:1px dashed var(--paper-edge); }
  .ar-inc-time { font-size:11px; color:var(--ink3); flex:1; }
  .ar-page .ar-btn-accept { padding:10px 22px; border-radius:980px; background:var(--green); color:#fff; font-size:13.5px; font-weight:700; box-shadow:0 2px 10px rgba(52,199,89,.35); transition:all .18s var(--ease); }
  .ar-page .ar-btn-accept:hover { transform:translateY(-1px); box-shadow:0 4px 14px rgba(52,199,89,.45); }
  .ar-page .ar-btn-accept:active { transform:scale(.96); box-shadow:0 1px 6px rgba(52,199,89,.35); }
  .ar-page .ar-btn-counter { padding:9px 16px; border-radius:980px; background:#fff; border:1.5px solid var(--brand); color:var(--brand-ink); font-size:12.5px; font-weight:600; transition:background .15s, transform .15s var(--ease); }
  .ar-page .ar-btn-counter:hover { background:var(--brand-soft); }
  .ar-page .ar-btn-counter:active { transform:scale(.96); }
  .ar-page .ar-btn-pass { padding:9px 14px; border-radius:980px; background:transparent; color:var(--ink3); font-size:12.5px; font-weight:500; transition:background .15s, color .15s, transform .15s var(--ease); }
  .ar-page .ar-btn-pass:hover { background:var(--surface); color:#FF3B30; }
  .ar-page .ar-btn-pass:active { transform:scale(.96); }

  /* the offer ticket */
  .ar-build { flex:1; overflow-y:auto; padding:6px 18px 10px; }
  .ar-ticket { background:var(--paper); border:1px solid var(--paper-edge); border-radius:14px; overflow:hidden; box-shadow:0 1px 0 var(--paper-edge),0 10px 30px rgba(120,80,20,.07); }
  .ar-ticket-top { display:flex; align-items:center; gap:8px; padding:12px 16px; border-bottom:1px dashed var(--paper-edge); }
  .ar-ticket-num { font-family:var(--mono); font-size:12px; color:var(--ink2); }
  .ar-ticket-num b { color:var(--ink); }
  .ar-ticket-stamp { margin-left:auto; font-size:10px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:var(--ink3); border:1.5px solid var(--disabled); border-radius:6px; padding:3px 8px; transform:rotate(-3deg); opacity:.8; }
  .ar-ticket-grid { display:flex; align-items:stretch; }
  .ar-tcol { flex:1; min-width:0; padding:14px 16px; }
  .ar-tcol.get { background:rgba(52,199,89,.05); }
  .ar-tcol-k { font-size:10px; font-weight:700; letter-spacing:.09em; text-transform:uppercase; margin-bottom:12px; }
  .ar-tcol.get .ar-tcol-k { color:var(--green-ink); }
  .ar-tcol.give .ar-tcol-k { color:var(--ink2); }
  .ar-tdiv { flex:none; width:40px; display:flex; align-items:center; justify-content:center; border-left:1px dashed var(--paper-edge); border-right:1px dashed var(--paper-edge); }
  .ar-tdiv span { font-size:10px; font-weight:700; letter-spacing:.06em; color:var(--ink3); writing-mode:vertical-rl; transform:rotate(180deg); }
  .ar-titem { display:flex; align-items:center; gap:9px; background:#fff; border:1px solid var(--paper-edge); border-radius:10px; padding:7px 9px; margin-bottom:7px; cursor:pointer; animation:artiin .32s var(--ease) both; }
  @keyframes artiin { from { opacity:0; transform:translateY(6px) scale(.94); } to { opacity:1; transform:none; } }
  .ar-ti-name { font-size:12.5px; font-weight:600; color:var(--ink); }
  .ar-ti-meta { font-size:10px; color:var(--ink3); }
  .ar-ti-x { margin-left:auto; font-size:14px; color:var(--ink3); flex:none; }
  .ar-tslot { border:1.5px dashed var(--paper-edge); border-radius:10px; padding:13px; text-align:center; font-size:12px; color:var(--ink3); cursor:pointer; }
  .ar-tslot:active { background:var(--surface); }
  .ar-ticket-foot { display:flex; align-items:center; gap:10px; padding:11px 16px; border-top:1px dashed var(--paper-edge); background:#fff; }
  .ar-tf-read { font-size:12.5px; color:var(--ink2); line-height:1.4; }

  .ar-actions { flex:none; padding:12px 18px 16px; display:flex; gap:10px; border-top:1px solid rgba(0,0,0,.04); }
  .ar-page .ar-send { flex:2; background:var(--ink); color:#fff; padding:14px; border-radius:980px; font-size:15px; font-weight:600; display:flex; align-items:center; justify-content:center; gap:8px; box-shadow:0 3px 12px rgba(0,0,0,.18); transition:all .25s var(--ease); }
  .ar-page .ar-send:hover:not(:disabled) { transform:translateY(-1px); opacity:.92; }
  .ar-page .ar-send:active:not(:disabled) { transform:scale(.97); opacity:.92; }
  .ar-page .ar-send:disabled { background:var(--disabled); box-shadow:none; cursor:default; }
  .ar-page .ar-clear { flex:1; background:#fff; border:1px solid var(--paper-edge); color:var(--ink2); padding:14px; border-radius:980px; font-size:15px; font-weight:500; transition:background .15s, transform .15s var(--ease); }
  .ar-page .ar-clear:hover { background:var(--surface); }
  .ar-page .ar-clear:active { transform:scale(.97); }

  /* mini-tray: the trade you're building, always visible while browsing rosters */
  .ar-minitray { display:none; }

  /* time's-up overlay — covers the beat between 0:00 and the receipt screen */
  .ar-timeup { position:absolute; inset:0; z-index:50; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:10px;
    background:rgba(255,253,248,.94); backdrop-filter:blur(3px); animation:artoast .25s var(--ease) both; }
  .ar-timeup .big { font-family:var(--mono); font-size:30px; font-weight:800; letter-spacing:.1em; color:var(--ink); }
  .ar-timeup .sub { font-size:14px; color:var(--ink2); display:flex; align-items:center; gap:8px; }
  .ar-timeup .spin { width:14px; height:14px; border:2px solid var(--disabled); border-top-color:var(--brand); border-radius:50%; animation:arspin .8s linear infinite; }
  @keyframes arspin { to { transform:rotate(360deg); } }

  /* deal served flash */
  .ar-served-flash { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; pointer-events:none; z-index:30; }
  .ar-served-stamp { font-family:var(--mono); font-size:34px; font-weight:800; letter-spacing:.12em; color:var(--green);
    border:4px solid var(--green); border-radius:14px; padding:14px 30px; background:rgba(255,255,255,.92);
    transform:rotate(-9deg); box-shadow:0 12px 40px rgba(52,199,89,.3); animation:arstamp 2.2s var(--ease) both; }
  .ar-served-stamp small { display:block; font-size:12px; letter-spacing:.18em; text-align:center; margin-top:4px; color:var(--green-ink); }
  @keyframes arstamp { 0% { opacity:0; transform:rotate(-9deg) scale(2.2); } 14% { opacity:1; transform:rotate(-9deg) scale(.95); } 20% { transform:rotate(-9deg) scale(1); } 82% { opacity:1; } 100% { opacity:0; transform:rotate(-9deg) scale(1); } }
  .ar-confetti { position:absolute; inset:0; pointer-events:none; }
  .ar-confetti i { position:absolute; top:50%; left:50%; width:7px; height:7px; border-radius:50%; background:var(--green);
    transform:rotate(var(--deg)) translate(0,0); animation:arconfetti 1.1s var(--ease) both; }
  .ar-confetti i:nth-child(even) { background:var(--brand); }
  @keyframes arconfetti {
    0% { opacity:0; transform:rotate(var(--deg)) translate(0,0) scale(.5); }
    30% { opacity:1; }
    100% { opacity:0; transform:rotate(var(--deg)) translate(96px,0) scale(1); }
  }

  /* chat */
  .ar-chat { flex:none; height:168px; border-top:1px solid var(--hair); display:flex; flex-direction:column; background:#fff; }
  .ar-chat-k { padding:9px 14px; font-size:9px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:var(--ink3); border-bottom:1px solid var(--surface); }
  .ar-chat-msgs { flex:1; overflow-y:auto; padding:10px 14px; }
  .ar-cmsg { margin-bottom:9px; animation:armsgin .22s var(--ease) both; }
  @keyframes armsgin { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:none; } }
  .ar-cmsg .who { font-size:10px; font-weight:700; color:var(--ink3); margin-bottom:2px; }
  .ar-cmsg .tx { font-size:13px; color:var(--ink); line-height:1.4; }
  .ar-chat-in { padding:8px 12px; border-top:1px solid var(--surface); display:flex; gap:8px; }
  .ar-chat-in input { flex:1; background:var(--surface); border:none; border-radius:980px; padding:9px 14px; font-size:13px; color:var(--ink); outline:none; font-family:inherit; }
  .ar-page .ar-chat-send { width:32px; height:32px; border-radius:50%; background:var(--ink); color:#fff; font-size:14px; display:flex; align-items:center; justify-content:center; flex:none; transition:transform .15s var(--ease); }
  .ar-page .ar-chat-send:active { transform:scale(.9); }
  .ar-chat-pane { display:none; }

  /* chat toast — a message you'd otherwise miss */
  .ar-chat-toast { position:absolute; top:10px; left:50%; transform:translateX(-50%); z-index:40; max-width:92%;
    background:var(--ink); color:#fff; border-radius:14px; padding:10px 16px; box-shadow:0 10px 30px rgba(0,0,0,.3);
    cursor:pointer; animation:artoast .3s var(--ease) both; }
  @keyframes artoast { from { opacity:0; transform:translate(-50%,-8px); } to { opacity:1; transform:translate(-50%,0); } }
  .ar-chat-toast .who { font-size:10px; font-weight:700; color:var(--brand); margin-bottom:2px; }
  .ar-chat-toast .tx { font-size:13px; line-height:1.35; }

  /* quit modal */
  .ar-modal-bd { position:fixed; inset:0; background:rgba(28,21,12,.45); display:flex; align-items:center; justify-content:center; z-index:1000; }
  .ar-modal { background:#fff; border-radius:20px; padding:30px; max-width:360px; width:90%; text-align:center; box-shadow:0 24px 60px rgba(0,0,0,.2); }
  .ar-modal-title { font-size:19px; font-weight:600; color:var(--ink); margin-bottom:10px; }
  .ar-modal-body { font-size:15px; color:var(--ink2); line-height:1.5; margin-bottom:22px; }
  .ar-modal-actions { display:flex; gap:10px; justify-content:center; }
  .ar-page-modal .ar-modal-cancel, .ar-modal-cancel { padding:12px 24px; border-radius:980px; background:var(--surface); color:var(--ink); font-size:15px; font-weight:500; border:none; cursor:pointer; font-family:inherit; }
  .ar-modal-confirm { padding:12px 24px; border-radius:980px; background:#FF3B30; color:#fff; font-size:15px; font-weight:600; border:none; cursor:pointer; font-family:inherit; }

  /* mobile pane tabs (hidden on desktop) */
  .ar-mtabs { display:none; }

  @media (max-width:1080px) { .ar-menu { width:230px; } }

  /* ── Mobile: one pane at a time — You / Offer / Them ── */
  @media (max-width:900px) {
    .ar-page { height:calc(100vh - 124px); height:calc(100dvh - 124px); } /* topbar 60 + bottom tabs 64 */

    .ar-gamebar { height:56px; padding:0 10px; gap:6px; }
    .ar-gp { width:auto; min-width:0; flex:1; }
    .ar-gp-id .nm { font-size:12px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:96px; }
    .ar-gp-id .mt { display:none; }
    .ar-gp-ava { width:28px; height:28px; font-size:10px; }
    .ar-center-bar { flex:none; }
    .ar-timer { font-size:22px; }
    .ar-page .ar-quit { padding:6px 8px; }

    .ar-mtabs { display:flex; flex:none; border-bottom:1px solid var(--hair); background:#fff; }
    .ar-page .ar-mtabs button { flex:1; position:relative; padding:12px 4px; font-size:12.5px; font-weight:600; color:var(--ink3); border-bottom:2px solid transparent; min-height:44px; }
    .ar-page .ar-mtabs button.on { color:var(--ink); border-bottom-color:var(--brand); }
    .ar-mtabs .bdg { display:inline-flex; align-items:center; justify-content:center; margin-left:5px; background:var(--brand); color:#fff; font-size:10px; font-weight:700; min-width:16px; height:16px; border-radius:980px; padding:0 4px; }
    .ar-mtabs .dot { position:absolute; top:9px; margin-left:4px; width:7px; height:7px; border-radius:50%; background:var(--brand); }

    .ar-body { position:relative; }
    .ar-body > .ar-menu, .ar-body > .ar-center { display:none; }
    .ar-menu { width:100%; }
    .ar-menu.left, .ar-menu.right { border-left:none; border-right:none; }
    .ar-body.pane-you > .ar-menu.left { display:flex; }
    .ar-body.pane-ticket > .ar-center { display:flex; }
    .ar-body.pane-them > .ar-menu.right { display:flex; }

    /* chat is its own pane on mobile — full height, nothing crowds the offers */
    .ar-chat.desktop { display:none; }
    .ar-body.pane-chat > .ar-chat-pane { display:flex; flex:1; min-width:0; }
    .ar-chat-pane .ar-chat { display:flex; height:auto; flex:1; border-top:none; }

    /* offers own the pane: incoming shows fully, ticket scrolls below */
    .ar-incoming { max-height:none; overflow:visible; }

    /* the building trade rides the bottom of both roster panes */
    .ar-body.pane-you > .ar-minitray, .ar-body.pane-them > .ar-minitray { display:flex; }
    .ar-minitray { position:absolute; left:0; right:0; bottom:0; z-index:20; align-items:center; gap:8px;
      background:var(--paper); border-top:1px solid var(--paper-edge); box-shadow:0 -6px 18px rgba(120,80,20,.08);
      padding:9px 12px calc(9px + env(safe-area-inset-bottom)); cursor:pointer;
      max-height:38dvh; overflow-y:auto; }
    .ar-mt-side { flex:1; min-width:0; display:flex; align-items:center; gap:5px; flex-wrap:wrap; row-gap:4px; }
    .ar-mt-side.get { justify-content:flex-end; }
    .ar-mt-mid, .ar-mt-go { align-self:center; }
    .ar-mt-k { font-size:8.5px; font-weight:800; letter-spacing:.07em; flex:none; }
    .ar-mt-side.give .ar-mt-k { color:var(--brand-ink); }
    .ar-mt-side.get .ar-mt-k { color:var(--green-ink); }
    .ar-mt-chip { display:inline-flex; align-items:center; gap:4px; background:#fff; border:1px solid var(--paper-edge);
      border-radius:980px; padding:3px 8px 3px 3px; flex:none; max-width:104px; }
    .ar-mt-chip .nm { font-size:10.5px; font-weight:600; color:var(--ink); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .ar-mt-empty { font-size:11px; color:var(--ink3); }
    .ar-mt-more { font-size:10px; font-weight:800; color:var(--ink2); background:var(--surface2); border-radius:980px; padding:4px 8px; flex:none; }
    .ar-mt-mid { flex:none; font-size:13px; color:var(--ink3); }
    .ar-mt-go { flex:none; font-size:10px; font-weight:700; color:var(--brand-ink); background:var(--brand-soft); border-radius:980px; padding:5px 10px; }
    .ar-body.pane-you .ar-scroll, .ar-body.pane-them .ar-scroll { padding-bottom:calc(var(--trayh, 72px) + 14px); }
    .ar-actions { padding:10px 12px calc(12px + env(safe-area-inset-bottom)); }
    .ar-page .ar-send { padding:13px; font-size:14px; }
    .ar-page .ar-clear { padding:13px; font-size:14px; flex:0 0 88px; }
    .ar-row { padding:10px; }        /* bigger touch targets */
    .ar-page .ar-fpill { padding:6px 11px; font-size:11px; }

    /* iOS auto-zooms any focused input under 16px — keep chat input at 16 */
    .ar-chat-in input { font-size:16px; }

    /* long usernames must never collide with the clock */
    .ar-gp-id { min-width:0; }
    .ar-gp-id .nm { max-width:88px; }
    .ar-center-bar { min-width:0; max-width:44%; }
    .ar-round { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:100%; }
  }

  @media (prefers-reduced-motion: reduce) {
    .ar-page *, .ar-page *::before, .ar-page *::after,
    .ar-modal-bd *, .ar-modal-bd *::before, .ar-modal-bd *::after {
      animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important;
    }
  }
`;

// ── helpers ──
function ordinal(n) { if (n === 1) return '1st'; if (n === 2) return '2nd'; if (n === 3) return '3rd'; return `${n}th`; }
function timeSince(date) { const s = Math.floor((Date.now() - new Date(date).getTime()) / 1000); if (s < 60) return `${s}s ago`; return `${Math.floor(s / 60)}m ago`; }
function pickMeta(pk) { const name = pk.originalOwnerName; const rec = pk.originalOwnerRecord; if (!name) return 'own'; const r = rec ? ` · ${rec.wins}-${rec.losses}` : ''; return `via ${name}${r}`; }
function leagueLabel(s) { if (!s) return null; const p = []; if (s.numTeams) p.push(`${s.numTeams}-team`); if (s.ppr === 1) p.push('PPR'); else if (s.ppr === 0.5) p.push('Half-PPR'); if (s.isSuperFlex) p.push('Superflex'); return p.join(' · ') || null; }
const POS_ORDER = { QB: 0, RB: 1, WR: 2, TE: 3, K: 4, DEF: 5 };
function sortRoster(players) { return [...(players || [])].sort((a, b) => { const pa = POS_ORDER[a.position] ?? 99, pb = POS_ORDER[b.position] ?? 99; if (pa !== pb) return pa - pb; return (b.fcValue ?? 0) - (a.fcValue ?? 0); }); }

// One identity for players AND picks (picks have no sleeperId — season/round/origin is the key)
function assetKey(a) {
  return a?.type === 'pick'
    ? `pk:${a.season}-${a.round}-${a.originalOwnerRosterId ?? ''}`
    : `pl:${a?.sleeperId}`;
}

// ── avatar ──
function Avatar({ asset, cls = '' }) {
  const [err, setErr] = useState(false);
  if (asset?.type === 'pick') {
    return <div className={`ar-av pick ${cls}`}><span>'{String(asset.season || '').slice(-2)}</span><span>{ordinal(asset.round)}</span></div>;
  }
  const src = asset?.sleeperId && !err ? `${SLEEPERCDN}${asset.sleeperId}.jpg` : null;
  return <div className={`ar-av ${cls}`}>{src ? <img src={src} alt="" onError={() => setErr(true)} /> : (asset?.name || '?').slice(0, 2).toUpperCase()}</div>;
}

function assetName(a) { return a.name || (a.type === 'pick' ? `${a.season} ${ordinal(a.round)}` : '—'); }
function assetMeta(a) { return a.type === 'pick' ? pickMeta(a) : [a.position, a.team].filter(Boolean).join(' · '); }

// ── ticket column ──
function TicketColumn({ side, label, assets, onRemove, emptyHint, onEmptyTap }) {
  return (
    <div className={`ar-tcol ${side}`}>
      <div className="ar-tcol-k">{label}</div>
      {assets.map((a, i) => (
        <div key={assetKey(a)} className="ar-titem" onClick={onRemove ? () => onRemove(i) : undefined}>
          <Avatar asset={a} cls="lg" />
          <div><div className="ar-ti-name">{assetName(a)}</div><div className="ar-ti-meta">{assetMeta(a)}</div></div>
          {onRemove && <span className="ar-ti-x">×</span>}
        </div>
      ))}
      <div className="ar-tslot" onClick={onEmptyTap}>{emptyHint}</div>
    </div>
  );
}

// ── incoming offer card — YOU GIVE (left, matches your pane) ⇄ YOU GET (right) ──
function IncomingCard({ offer, onAccept, onCounter, onPass }) {
  return (
    <div className="ar-inc-card">
      <div className="ar-inc-strip" />
      <div className="ar-inc-from">THEIR OFFER</div>
      <div className="ar-inc-grid">
        <div className="ar-inc-col give">
          <div className="ar-inc-col-k">You'd give</div>
          {(offer.askingAssets || []).map((a) => (
            <div key={assetKey(a)} className="ar-inc-asset"><Avatar asset={a} cls="sm" /><div><div className="ar-ia-name">{assetName(a)}</div><div className="ar-ia-meta">{assetMeta(a)}</div></div></div>
          ))}
        </div>
        <div className="ar-inc-for">⇄</div>
        <div className="ar-inc-col get">
          <div className="ar-inc-col-k">You'd get</div>
          {(offer.offeringAssets || []).map((a) => (
            <div key={assetKey(a)} className="ar-inc-asset"><Avatar asset={a} cls="sm" /><div><div className="ar-ia-name">{assetName(a)}</div><div className="ar-ia-meta">{assetMeta(a)}</div></div></div>
          ))}
        </div>
      </div>
      <div className="ar-inc-actions">
        <span className="ar-inc-time">{offer.sentAt ? timeSince(offer.sentAt) : 'just now'}</span>
        <button className="ar-btn-pass" onClick={onPass}>Decline</button>
        <button className="ar-btn-counter" onClick={onCounter}>↩ Counter</button>
        <button className="ar-btn-accept" onClick={onAccept}>✓ Accept</button>
      </div>
    </div>
  );
}

// ── roster pane ──
function Menu({ side, kicker, name, avatar, isBot, isMe, players, picks, filter, setFilter, selectedKeys, onTap, tagPos, tagKind, tagLabel, children }) {
  return (
    <div className={`ar-menu ${side}`}>
      <div className="ar-menu-head">
        <div className={`ar-mh-ava${isBot ? ' taco' : ''}`}>
          {isBot ? '🌮' : (avatar
            ? <img src={`https://sleepercdn.com/avatars/thumbs/${avatar}`} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
            : (name || '?').charAt(0).toUpperCase())}
        </div>
        <div className="ar-mh-id">
          <div className="ar-mh-row">
            <span className={`ar-mh-tag ${isMe ? 'you' : 'them'}`}>{isMe ? 'YOU' : 'THEM'}</span>
            <div className="ar-mh-name">{name}</div>
          </div>
          <div className="ar-mh-k">{kicker}</div>
        </div>
      </div>
      <div className="ar-filters">
        {['ALL', 'QB', 'RB', 'WR', 'TE', 'PICKS'].map((p) => (
          <button key={p} className={`ar-fpill${filter === p ? ' on' : ''}`} onClick={() => setFilter(p)}>{p}</button>
        ))}
      </div>
      <div className="ar-scroll">
        {filter !== 'PICKS' && (() => {
          const groups = {};
          players.filter((p) => filter === 'ALL' || p.position === filter).forEach((p) => { (groups[p.position] = groups[p.position] || []).push(p); });
          return ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'].filter((pos) => groups[pos]?.length).map((pos) => (
            <div key={pos} className="ar-posgroup">
              <div className="ar-poslabel">{pos}</div>
              {groups[pos].map((p, i) => {
                const selected = selectedKeys.has(assetKey(p));
                return (
                  <div key={p.sleeperId || i} className={`ar-row${selected ? ' in' : ''}`} onClick={() => onTap(p)}>
                    <Avatar asset={p} />
                    <div className="ar-p-info"><div className="ar-p-name">{p.name || p.sleeperId}</div><div className="ar-p-meta">{[p.position, p.team].filter(Boolean).join(' · ')}</div></div>
                    {tagPos?.has(p.position) && (p.fcValue || 0) >= 1000 && <span className={`ar-row-tag ${tagKind}`}>{tagLabel}</span>}
                    <span className="ar-add">{selected ? '✓' : '+'}</span>
                  </div>
                );
              })}
            </div>
          ));
        })()}
        {(filter === 'ALL' || filter === 'PICKS') && picks.length > 0 && (
          <div className="ar-posgroup">
            <div className="ar-poslabel">Picks</div>
            {picks.map((pk, i) => {
              const pkAsset = { ...pk, type: 'pick' };
              const selected = selectedKeys.has(assetKey(pkAsset));
              return (
                <div key={assetKey(pkAsset) + i} className={`ar-row${selected ? ' in' : ''}`} onClick={() => onTap(pkAsset)}>
                  <Avatar asset={pkAsset} />
                  <div className="ar-p-info"><div className="ar-p-name">{pk.season} {ordinal(pk.round)}</div><div className="ar-p-meta">{pickMeta(pk)}</div></div>
                  <span className="ar-add">{selected ? '✓' : '+'}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

// ── chat dock (rendered twice: desktop under your roster, mobile on the offer pane) ──
function ChatDock({ variant, messages, text, setText, onSend, placeholder }) {
  const bottomRef = useRef(null);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  return (
    <div className={`ar-chat ${variant}`}>
      <div className="ar-chat-k">Chat</div>
      <div className="ar-chat-msgs">
        {messages.map((m, i) => (<div key={i} className="ar-cmsg"><div className="who">{m.from}</div><div className="tx">{m.text}</div></div>))}
        <div ref={bottomRef} />
      </div>
      <div className="ar-chat-in">
        <input placeholder={placeholder} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') onSend(); }} />
        <button className="ar-chat-send" onClick={onSend}>→</button>
      </div>
    </div>
  );
}

export default function TradeArenaRoom() {
  const { sessionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const initData = location.state || {};
  const [sessionData, setSessionData] = useState(initData);
  const [pendingOffers, setPendingOffers] = useState([]);
  const [myRoster, setMyRoster] = useState(initData.myRosterData?.players || []);
  const [myPicks, setMyPicks] = useState(initData.myRosterData?.picks || []);
  const [myAvatar, setMyAvatar] = useState(initData.myAvatar || null);
  const [opponentAvatar, setOpponentAvatar] = useState(initData.opponentAvatar || null);

  const [sending, setSending] = useState([]);   // assets I GIVE (from my roster)
  const [asking, setAsking] = useState([]);      // assets I GET (from theirs)

  const [showQuit, setShowQuit] = useState(false);
  const [counterMode, setCounterMode] = useState(false);
  const [counterOffer, setCounterOffer] = useState(null);
  const [counterSending, setCounterSending] = useState([]);
  const [counterAsking, setCounterAsking] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatText, setChatText] = useState('');
  const [chatToast, setChatToast] = useState(null);
  const [myFilter, setMyFilter] = useState('ALL');
  const [oppFilter, setOppFilter] = useState('ALL');
  const [timerSecs, setTimerSecs] = useState(SESSION_SECS);
  const [prepSecs, setPrepSecs] = useState(0);
  const [mobilePane, setMobilePane] = useState('ticket'); // you | ticket | them (≤900px)
  const [unreadChat, setUnreadChat] = useState(0);
  const [dealsCount, setDealsCount] = useState(0);
  const [servedFlash, setServedFlash] = useState(null);

  const timerRef = useRef(null);
  const socketRef = useRef(null);
  const deadlineRef = useRef(null);   // server-authoritative kitchen clock
  const startsAtRef = useRef(null);   // scouting ends / window opens
  const lastBeatRef = useRef(0);      // heartbeat throttle
  const oppIsBotRef = useRef(!!initData.opponentIsBot);
  const flashTimerRef = useRef(null);
  const trayRef = useRef(null);
  const toastTimerRef = useRef(null);
  const mobilePaneRef = useRef('ticket');
  mobilePaneRef.current = mobilePane;

  // Trust the server's clock (skew-corrected) — refresh-proof.
  const applyClock = useCallback((data) => {
    if (!data?.sessionEndsAt) return;
    const skew = data.serverNow ? new Date(data.serverNow).getTime() - Date.now() : 0;
    deadlineRef.current = new Date(data.sessionEndsAt).getTime() - skew;
    startsAtRef.current = data.startsAt ? new Date(data.startsAt).getTime() - skew : null;
  }, []);

  const activeSending = counterMode ? counterSending : sending; // GIVE
  const activeAsking = counterMode ? counterAsking : asking;      // GET

  const opponentPicks = sessionData.opponentRosterData?.picks || [];
  const myUsername = user?.sleeperUsername || user?.email?.split('@')[0] || 'You';
  const oppName = sessionData.opponentSleeperUsername || sessionData.opponentUsername || 'them';

  const givenKeys = useMemo(() => new Set((activeSending || []).map(assetKey)), [activeSending]);
  const askedKeys = useMemo(() => new Set((activeAsking || []).map(assetKey)), [activeAsking]);

  const myRosterSorted = useMemo(() => sortRoster(myRoster), [myRoster]);
  const scout = useMemo(() => buildScout({
    myPlayers: myRoster,
    oppPlayers: sessionData.opponentRosterData?.players,
    myPicks, oppPicks: sessionData.opponentRosterData?.picks || [],
    oppTc: sessionData.opponentRosterData?.teamContext,
  }), [myRoster, myPicks, sessionData.opponentRosterData]);
  const myExcessPos = useMemo(() => new Set(scout.mySurplus), [scout]);
  const theirTargetPos = useMemo(() => new Set(scout.theirSurplus), [scout]);
  const oppRosterSorted = useMemo(() => sortRoster(sessionData.opponentRosterData?.players || []), [sessionData.opponentRosterData]);
  const incomingOffers = pendingOffers.filter((o) => o.receiverUserId === user?.id);
  const timerStr = `${Math.floor(timerSecs / 60)}:${String(timerSecs % 60).padStart(2, '0')}`;

  // timer — driven by the server deadline; falls back to a local countdown
  useEffect(() => {
    applyClock(initData);
    timerRef.current = setInterval(() => {
      const now = Date.now();
      const inPrep = startsAtRef.current && now < startsAtRef.current;
      setPrepSecs(inPrep ? Math.max(0, Math.ceil((startsAtRef.current - now) / 1000)) : 0);
      if (deadlineRef.current) {
        setTimerSecs(inPrep
          ? Math.round((deadlineRef.current - startsAtRef.current) / 1000)
          : Math.max(0, Math.ceil((deadlineRef.current - now) / 1000)));
      } else {
        setTimerSecs((s) => (inPrep ? s : Math.max(0, s - 1)));
      }
    }, 400);
    return () => { clearInterval(timerRef.current); clearTimeout(flashTimerRef.current); clearTimeout(toastTimerRef.current); };
  }, []); // eslint-disable-line

  // keep roster-list padding in sync with the tray's real (wrapping) height
  useEffect(() => {
    const el = trayRef.current;
    const body = el?.parentElement;
    if (!body) return;
    const apply = () => body.style.setProperty('--trayh', `${el.offsetHeight}px`);
    apply();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    ro?.observe(el);
    return () => { ro?.disconnect(); body.style.setProperty('--trayh', '0px'); };
  }, [activeSending.length, activeAsking.length]);

  // heartbeat — throttled (raw mousemove would emit hundreds of events/sec)
  useEffect(() => {
    const send = () => {
      const t = Date.now();
      if (t - lastBeatRef.current < 5000) return;
      lastBeatRef.current = t;
      socketRef.current?.emit('arena:heartbeat', { sessionId });
    };
    const events = ['mousemove', 'scroll', 'click', 'keypress', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, send, { passive: true }));
    const iv = setInterval(() => { lastBeatRef.current = 0; send(); }, 15000);
    return () => { events.forEach((e) => window.removeEventListener(e, send)); clearInterval(iv); };
  }, [sessionId]);

  // socket
  useEffect(() => {
    if (!user) return;
    const sock = getArenaSocket(user.id, user.sleeperUsername);
    socketRef.current = sock;
    sock.emit('arena:join_session', { sessionId });

    const applyRosters = (data) => {
      setMyRoster(data.myRosterData?.players || []);
      setMyPicks(data.myRosterData?.picks || []);
    };
    const onState = (data) => { applyClock(data); oppIsBotRef.current = !!data.opponentIsBot; setSessionData(data); applyRosters(data); setPendingOffers(data.pendingOffers || []); if (data.myAvatar) setMyAvatar(data.myAvatar); if (data.opponentAvatar) setOpponentAvatar(data.opponentAvatar); };
    const onMatch = (data) => { applyClock(data); oppIsBotRef.current = !!data.opponentIsBot; setSessionData(data); applyRosters(data); if (data.myAvatar !== undefined) setMyAvatar(data.myAvatar); if (data.opponentAvatar !== undefined) setOpponentAvatar(data.opponentAvatar); };
    const onOffer = ({ offer }) => {
      setPendingOffers((prev) => [offer, ...prev.filter((o) => o.id !== offer.id)]);
      if (offer?.receiverUserId === user?.id) navigator.vibrate?.(20);
    };
    const onResp = ({ offerId, response }) =>
      setPendingOffers((prev) => prev.filter((o) => o.id !== offerId || !['declined', 'expired', 'countered'].includes(response)));
    const onChat = ({ from, message }) => {
      setChatMessages((prev) => [...prev, { from, text: message }]);
      const isOpp = from !== (user?.sleeperUsername || '');
      if (!isOpp) return;
      const mobile = window.matchMedia('(max-width: 900px)').matches;
      const chatVisible = !mobile || mobilePaneRef.current === 'chat';
      if (!chatVisible) setUnreadChat((n) => n + 1);
      if (mobile && !chatVisible) {
        navigator.vibrate?.(15);
        setChatToast({ from, text: message });
        clearTimeout(toastTimerRef.current);
        toastTimerRef.current = setTimeout(() => setChatToast(null), 3800);
      }
    };
    // A deal landed — assets swap sides, the session keeps going.
    const onDeal = (data) => {
      navigator.vibrate?.([30, 60, 30]);
      const mine = data.player1UserId === user?.id ? data.player1RosterData : data.player2RosterData;
      const theirs = data.player1UserId === user?.id ? data.player2RosterData : data.player1RosterData;
      setSessionData((prev) => ({ ...prev, myRosterData: mine, opponentRosterData: theirs }));
      setMyRoster(mine?.players || []);
      setMyPicks(mine?.picks || []);
      setPendingOffers([]);       // every other offer just went stale
      setSending([]); setAsking([]);
      setCounterMode(false); setCounterOffer(null); setCounterSending([]); setCounterAsking([]);
      setDealsCount(data.dealNumber || 1);
      setServedFlash({ num: data.dealNumber || 1 });
      clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => setServedFlash(null), 2300);
    };
    const onEnded = (data) => {
      navigate(`/taco-stand/result/${sessionId}`, { state: { ...data, sessionId, opponentUsername: oppName, opponentIsBot: oppIsBotRef.current } });
    };

    sock.on('arena:session_state', onState);
    sock.on('arena:match_found', onMatch);
    sock.on('arena:offer_received', onOffer);
    sock.on('arena:offer_response', onResp);
    sock.on('arena:chat', onChat);
    sock.on('arena:deal_cooked', onDeal);
    sock.on('arena:session_ended', onEnded);

    return () => { sock.off('arena:session_state', onState); sock.off('arena:match_found', onMatch); sock.off('arena:offer_received', onOffer); sock.off('arena:offer_response', onResp); sock.off('arena:chat', onChat); sock.off('arena:deal_cooked', onDeal); sock.off('arena:session_ended', onEnded); };
  }, [user, sessionId, navigate]); // eslint-disable-line

  // handlers — tap to add, tap again to remove
  const toggleIn = (setter) => (asset) => {
    const k = assetKey(asset);
    setter((prev) => (prev.some((a) => assetKey(a) === k) ? prev.filter((a) => assetKey(a) !== k) : [...prev, asset]));
  };
  const toggleGive = (asset) => (counterMode ? toggleIn(setCounterSending)(asset) : toggleIn(setSending)(asset));
  const toggleGet = (asset) => (counterMode ? toggleIn(setCounterAsking)(asset) : toggleIn(setAsking)(asset));

  const handleSend = () => {
    const payload = counterMode
      ? { offeringAssets: counterSending, askingAssets: counterAsking, counterOfOfferId: counterOffer?.id }
      : { offeringAssets: sending, askingAssets: asking };
    if (!payload.offeringAssets.length && !payload.askingAssets.length) return;
    socketRef.current?.emit('arena:send_offer', { sessionId, ...payload });
    if (counterMode) { setCounterMode(false); setCounterOffer(null); setCounterSending([]); setCounterAsking([]); }
    else { setSending([]); setAsking([]); }
  };

  const startCounter = (offer) => { setCounterOffer(offer); setCounterSending([...(offer.askingAssets || [])]); setCounterAsking([...(offer.offeringAssets || [])]); setCounterMode(true); };
  const cancelCounter = () => { setCounterMode(false); setCounterOffer(null); setCounterSending([]); setCounterAsking([]); };
  const respond = (offerId, response) => { socketRef.current?.emit('arena:respond_offer', { sessionId, offerId, response }); if (response === 'declined') setPendingOffers((prev) => prev.filter((o) => o.id !== offerId)); };
  const confirmQuit = () => { socketRef.current?.emit('arena:quit_session', { sessionId }); setShowQuit(false); };
  const sendChat = () => { const t = chatText.trim(); if (!t) return; socketRef.current?.emit('arena:chat', { sessionId, message: t }); setChatText(''); };

  const ticketHasContent = activeAsking.length > 0 || activeSending.length > 0;
  const chatEl = (variant) => (
    <ChatDock variant={variant} messages={chatMessages} text={chatText} setText={setChatText} onSend={sendChat} placeholder={`Message ${oppName}…`} />
  );

  return (
    <>
      <style>{styles}</style>

      {showQuit && (
        <div className="ar-modal-bd" onClick={() => setShowQuit(false)}>
          <div className="ar-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ar-modal-title">Leave this game?</div>
            <div className="ar-modal-body">{dealsCount > 0 ? `Your ${dealsCount} ${dealsCount === 1 ? 'deal' : 'deals'} are locked in — leaving just ends the session early.` : `Leaving before any deal counts as a walk-off: it snaps your streak and dings your standing.`}</div>
            <div className="ar-modal-actions">
              <button className="ar-modal-cancel" onClick={() => setShowQuit(false)}>Keep dealing</button>
              <button className="ar-modal-confirm" onClick={confirmQuit}>Leave</button>
            </div>
          </div>
        </div>
      )}

      <div className="ar-page">
        {/* game bar */}
        <div className="ar-gamebar">
          <div className="ar-gp" onClick={() => setMobilePane('you')}>
            <div className="ar-gp-ava">{myAvatar ? <img src={`https://sleepercdn.com/avatars/thumbs/${myAvatar}`} alt="" onError={(e) => { e.target.style.display = 'none'; }} /> : myUsername.charAt(0).toUpperCase()}</div>
            <div className="ar-gp-id"><div className="nm">{myUsername}</div><div className="mt">{leagueLabel(sessionData.myRosterData?.leagueSettings) || 'your team'}</div></div>
          </div>
          <div className="ar-center-bar">
            <div className={`ar-round${dealsCount > 0 ? ' served' : ''}`}>{prepSecs > 0 ? 'Scout their roster' : dealsCount > 0 ? `${dealsCount} ${dealsCount === 1 ? 'deal' : 'deals'} served — keep going` : 'Make your deals'}</div>
            {prepSecs > 0
              ? <div className="ar-timer" style={{ color: 'var(--brand)' }}>{`0:${String(prepSecs).padStart(2, '0')}`}<small>until the window opens</small></div>
              : <div className={`ar-timer${timerSecs < 30 ? ' urgent' : ''}`}>{timerStr}<small>{timerSecs === 0 ? 'closing…' : 'time left'}</small></div>}
          </div>
          <div className="ar-gp right" onClick={() => setMobilePane('them')}>
            <div className="ar-gp-id right"><div className="nm">{oppName}</div><div className="mt">{sessionData.opponentIsBot ? 'the machine' : (leagueLabel(sessionData.opponentRosterData?.leagueSettings) || 'their team')}</div></div>
            <div className="ar-gp-ava">{sessionData.opponentIsBot ? '🌮' : (opponentAvatar ? <img src={`https://sleepercdn.com/avatars/thumbs/${opponentAvatar}`} alt="" onError={(e) => { e.target.style.display = 'none'; }} /> : oppName.charAt(0).toUpperCase())}</div>
            <button className="ar-quit" onClick={(e) => { e.stopPropagation(); setShowQuit(true); }}>Leave</button>
          </div>
        </div>

        {/* mobile pane tabs */}
        <div className="ar-mtabs">
          <button className={mobilePane === 'you' ? 'on' : ''} onClick={() => setMobilePane('you')}>You</button>
          <button className={mobilePane === 'ticket' ? 'on' : ''} onClick={() => setMobilePane('ticket')}>
            Offer{incomingOffers.length > 0 && <span className="bdg">{incomingOffers.length}</span>}
          </button>
          <button className={mobilePane === 'them' ? 'on' : ''} onClick={() => setMobilePane('them')}>{sessionData.opponentIsBot ? 'Taco' : 'Them'}</button>
          <button className={mobilePane === 'chat' ? 'on' : ''} onClick={() => { setMobilePane('chat'); setUnreadChat(0); }}>
            Chat{unreadChat > 0 && <span className="bdg">{unreadChat}</span>}
          </button>
        </div>

        {/* body */}
        <div className={`ar-body pane-${mobilePane}`}>
          {/* LEFT: your roster → what you GIVE (chat docks here on desktop) */}
          <Menu side="left" kicker="Tap a player to give" name={myUsername} avatar={myAvatar} isMe
            tagPos={myExcessPos} tagKind="excess" tagLabel="SURPLUS"
            players={myRosterSorted} picks={myPicks} filter={myFilter} setFilter={setMyFilter} selectedKeys={givenKeys} onTap={toggleGive}>
            {chatEl('desktop')}
          </Menu>

          {/* CENTER: offers */}
          <div className="ar-center">
            {chatToast && (
              <div className="ar-chat-toast" onClick={() => { setChatToast(null); setMobilePane('chat'); setUnreadChat(0); }}>
                <div className="who">{chatToast.from}</div>
                <div className="tx">{chatToast.text}</div>
              </div>
            )}
            {timerSecs === 0 && prepSecs === 0 && (
              <div className="ar-timeup">
                <div className="big">TIME</div>
                <div className="sub"><span className="spin" /> Printing your receipts…</div>
              </div>
            )}
            {servedFlash && (
              <div className="ar-served-flash">
                <div className="ar-confetti">
                  {[0, 60, 120, 180, 240, 300].map((deg) => (
                    <i key={deg} style={{ '--deg': `${deg}deg` }} />
                  ))}
                </div>
                <div className="ar-served-stamp">SERVED<small>DEAL #{servedFlash.num} · KEEP GOING</small></div>
              </div>
            )}

            {counterMode && (
              <div className="ar-counter-banner">Counter — edit either side, then send<button className="ar-counter-cancel" onClick={cancelCounter}>Cancel</button></div>
            )}

            {!counterMode && (
              <div className="ar-incoming">
                <div className="ar-inc-head"><span className="ar-inc-k">Offers from {oppName}</span>{incomingOffers.length > 0 && <span className="ar-inc-count">{incomingOffers.length}</span>}</div>
                {incomingOffers.length === 0 ? (
                  <div className="ar-inc-empty">{prepSecs > 0 ? 'Scout the rosters and draft your first offer — the window opens soon.' : 'Nothing yet. Build an offer below, or wait for theirs.'}</div>
                ) : incomingOffers.map((offer) => (
                  <IncomingCard key={offer.id} offer={offer}
                    onAccept={() => respond(offer.id, 'accepted')} onCounter={() => startCounter(offer)} onPass={() => respond(offer.id, 'declined')} />
                ))}
              </div>
            )}

            <div className="ar-build">
              <div className="ar-ticket">
                <div className="ar-ticket-top">
                  <span className="ar-ticket-num"><b>{counterMode ? 'YOUR COUNTER' : 'YOUR OFFER'}</b></span>
                  <span className="ar-ticket-stamp">{counterMode ? 'Counter' : 'Draft'}</span>
                </div>
                <div className="ar-ticket-grid">
                  <TicketColumn side="give" label="You give" assets={activeSending}
                    onRemove={(i) => (counterMode ? setCounterSending((p) => p.filter((_, j) => j !== i)) : setSending((p) => p.filter((_, j) => j !== i)))}
                    emptyHint="＋ tap your roster" onEmptyTap={() => setMobilePane('you')} />
                  <div className="ar-tdiv"><span>— FOR —</span></div>
                  <TicketColumn side="get" label="🟢 You get" assets={activeAsking}
                    onRemove={(i) => (counterMode ? setCounterAsking((p) => p.filter((_, j) => j !== i)) : setAsking((p) => p.filter((_, j) => j !== i)))}
                    emptyHint="＋ tap their roster" onEmptyTap={() => setMobilePane('them')} />
                </div>
              </div>
            </div>

            <div className="ar-actions">
              <button className="ar-send" disabled={!ticketHasContent || prepSecs > 0} onClick={handleSend}>{prepSecs > 0 ? `Window opens in 0:${String(prepSecs).padStart(2, '0')}` : counterMode ? 'Send counter' : 'Send offer'} <span>→</span></button>
              <button className="ar-clear" onClick={() => (counterMode ? cancelCounter() : (setSending([]), setAsking([])))}>{counterMode ? 'Cancel' : 'Clear'}</button>
            </div>

          </div>

          {/* mini-tray: your in-progress trade, pinned while browsing rosters (mobile) */}
          {(activeSending.length > 0 || activeAsking.length > 0) && (
            <div className="ar-minitray" ref={trayRef} onClick={() => setMobilePane('ticket')}>
              <div className="ar-mt-side give">
                <span className="ar-mt-k">GIVE</span>
                {activeSending.length === 0 ? <span className="ar-mt-empty">—</span> : activeSending.slice(0, 2).map((a) => (
                  <span key={assetKey(a)} className="ar-mt-chip"><Avatar asset={a} cls="xs" /><span className="nm">{(assetName(a) || '').split(' ').slice(-1)[0]}</span></span>
                ))}
                {activeSending.length > 2 && <span className="ar-mt-more">+{activeSending.length - 2}</span>}
              </div>
              <span className="ar-mt-mid">⇄</span>
              <div className="ar-mt-side get">
                <span className="ar-mt-k">GET</span>
                {activeAsking.length === 0 ? <span className="ar-mt-empty">—</span> : activeAsking.slice(0, 2).map((a) => (
                  <span key={assetKey(a)} className="ar-mt-chip"><Avatar asset={a} cls="xs" /><span className="nm">{(assetName(a) || '').split(' ').slice(-1)[0]}</span></span>
                ))}
                {activeAsking.length > 2 && <span className="ar-mt-more">+{activeAsking.length - 2}</span>}
              </div>
              <span className="ar-mt-go">{counterMode ? 'Counter →' : 'Review →'}</span>
            </div>
          )}

          {/* CHAT: its own pane on mobile (desktop keeps the dock under your roster) */}
          <div className="ar-chat-pane">{chatEl('pane')}</div>

          {/* RIGHT: their roster → what you GET */}
          <Menu side="right" kicker="Tap a player to get" name={oppName} avatar={opponentAvatar} isBot={sessionData.opponentIsBot}
            tagPos={theirTargetPos} tagKind="target" tagLabel="TARGET"
            players={oppRosterSorted} picks={opponentPicks} filter={oppFilter} setFilter={setOppFilter} selectedKeys={askedKeys} onTap={toggleGet} />
        </div>
      </div>
    </>
  );
}
